import React, { useState, useEffect } from 'react';
import { Form, Button, Tabs, Badge } from 'antd';
import { FormConfig } from '../DynamicForm';
import { FixedDeviceForm } from './';
import { YingshiIntegration, YingshiCamera } from '../YingshiCloud';

interface DeviceFormProps {
  form: any;
  initialValues: Record<string, any>;
  onFinish: (values: Record<string, any>) => void;
  onCancel: () => void;
  isEditing: boolean;
  formConfig: FormConfig | null;
  onDeviceTypeChange: (value: number) => void;
  editingDevice: any | null;
}

const DeviceForm: React.FC<DeviceFormProps> = ({
  form,
  initialValues,
  onFinish,
  onCancel,
  isEditing,
  formConfig,
  onDeviceTypeChange,
  editingDevice,
}) => {
  const [activeTabKey, setActiveTabKey] = useState<string>("basic");
  const [selectedCameras, setSelectedCameras] = useState<YingshiCamera[]>([]);
  
  // Set active tab based on device type and existing channels
  // Only run this effect when initialValues or editingDevice changes
  useEffect(() => {
    const deviceType = initialValues.deviceType;
    
    // If editing a video device with existing channels, switch to the Yingshi tab
    if (deviceType === 3 && editingDevice?.exData?.channel && Array.isArray(editingDevice.exData.channel) && editingDevice.exData.channel.length > 0) {
      setActiveTabKey("yingshi");
    } else if (deviceType === 3) {
      // For new video devices, also switch to Yingshi tab
      setActiveTabKey("yingshi");
    } else {
      setActiveTabKey("basic");
    }
  }, [initialValues.deviceType, editingDevice]);

  // Handle camera selection from Yingshi integration
  const handleCamerasSelected = (cameras: YingshiCamera[]) => {
    setSelectedCameras(cameras);
  };

  const buildChannelPayload = (cameras: YingshiCamera[]) =>
    cameras.map((camera) => ({
      deviceSerial: camera.deviceSerial,
      channelNo: camera.channelNo,
      channelName: camera.channelName,
      status: camera.status,
    }));

  const getExtendedFieldKeys = (): Set<string> => {
    const fixedKeys = new Set([
      "deviceCode",
      "deviceName",
      "deviceType",
      "area",
      "remark",
      "device_code",
      "device_name",
      "device_type",
    ]);

    const keys = new Set<string>();
    formConfig?.fields?.forEach((field) => {
      if (!fixedKeys.has(field.key)) {
        keys.add(field.key);
      }
    });
    return keys;
  };

  const extractExtendedFields = (
    values: Record<string, any>
  ): Record<string, any> => {
    const standardFields = new Set([
      "deviceCode",
      "deviceName",
      "deviceType",
      "deviceFactory",
      "thirdType",
      "beian",
      "latitude",
      "longitude",
      "price",
      "isPay",
      "remark",
      "area",
      "exData",
      "appKey",
      "channel",
      "device_code",
      "device_name",
      "device_type",
    ]);

    const extendedFields: Record<string, any> = {};
    const configuredKeys = getExtendedFieldKeys();

    Object.entries(values).forEach(([key, value]) => {
      if (
        value !== undefined &&
        !standardFields.has(key) &&
        (configuredKeys.size === 0 || configuredKeys.has(key))
      ) {
        extendedFields[key] = value;
      }
    });

    return extendedFields;
  };

  // Handle form submission
  const handleSubmit = async () => {
    try {
      await form.validateFields();
    } catch {
      return;
    }

    const currentValues = form.getFieldsValue();
    const {
      exData: nestedExData,
      appKey: _appKey,
      channel: _channel,
      ...restValues
    } = currentValues;
    const extendedFields = extractExtendedFields(currentValues);
    const extendedKeys = new Set(Object.keys(extendedFields));
    const baseValues = Object.fromEntries(
      Object.entries(restValues).filter(([key]) => !extendedKeys.has(key))
    );

    if (currentValues.deviceType === 3) {
      const yingshiAppKey =
        form.getFieldValue("appKey") ||
        editingDevice?.exData?.appKey ||
        nestedExData?.appKey;
      const channels =
        selectedCameras.length > 0
          ? buildChannelPayload(selectedCameras)
          : editingDevice?.exData?.channel || nestedExData?.channel || [];

      const values = {
        ...baseValues,
        exData: {
          ...extendedFields,
          ...(nestedExData &&
          typeof nestedExData === "object" &&
          !Array.isArray(nestedExData) &&
          !nestedExData.exData
            ? Object.fromEntries(
                Object.entries(nestedExData).filter(
                  ([key]) => key !== "appKey" && key !== "channel"
                )
              )
            : {}),
          appKey: yingshiAppKey,
          channel: channels,
        },
      };

      onFinish(values);
      return;
    }

    const cleanValues = { ...baseValues, ...extendedFields };
    if (nestedExData?.exData) {
      cleanValues.exData = nestedExData.exData;
    } else if (nestedExData) {
      cleanValues.exData = nestedExData;
    }
    onFinish(cleanValues);
  };

  // Handle device type change
  const handleDeviceTypeChange = (value: number) => {
    // Call the parent handler
    onDeviceTypeChange(value);
    
    // If changing to video type, switch to Yingshi tab
    if (value === 3) {
      setActiveTabKey("yingshi");
    } else {
      setActiveTabKey("basic");
    }
  };

  // Determine if the submit button should be disabled
  const isSubmitDisabled = () => {
    const deviceType = form.getFieldValue('deviceType');
    // Only disable if it's a video device, we're on the Yingshi tab, and no cameras are selected
    return deviceType === 3 && activeTabKey === "yingshi" && selectedCameras.length === 0;
  };

  return (
    <div>
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          ...initialValues,
          exData: undefined
        }}
        requiredMark={false}
        preserve={false}
      >
        {/* Hidden form item for exData with correct initial value */}
        <Form.Item 
          name="exData" 
          initialValue={initialValues.exData?.exData || initialValues.exData || {}}
          hidden
        >
          <input />
        </Form.Item>

        <Tabs 
          activeKey={activeTabKey} 
          onChange={setActiveTabKey}
          items={[
            {
              key: "basic",
              label: "基本信息",
              children: (
                <FixedDeviceForm
                  form={form}
                  initialValues={initialValues}
                  formConfig={formConfig}
                  onDeviceTypeChange={handleDeviceTypeChange}
                />
              )
            },
            {
              key: "yingshi",
              label: (
                <Badge 
                  count={selectedCameras.length > 0 ? selectedCameras.length : 0} 
                  size="small" 
                  offset={[10, 0]}
                  style={{ backgroundColor: selectedCameras.length > 0 ? '#52c41a' : '#d9d9d9' }}
                >
                  萤石云集成
                </Badge>
              ),
              disabled: form.getFieldValue('deviceType') !== 3,
              children: (
                <YingshiIntegration
                  editingDevice={editingDevice}
                  onCamerasSelected={handleCamerasSelected}
                  form={form}
                />
              )
            }
          ]}
        />
        
        {/* Form Actions */}
        <div className="form-actions" style={{ marginTop: 24 }}>
          <Button onClick={onCancel}>取消</Button>
          <Button 
            type="primary" 
            onClick={handleSubmit}
            disabled={isSubmitDisabled()}
          >
            {isEditing ? "更新" : "创建"}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default DeviceForm; 